/**
 * A router for three routes.
 *
 * `/`, `/admin`, and `/s/:code` do not justify a routing library, and every
 * kilobyte of JavaScript is one a phone on classroom wifi has to fetch before a
 * student can join.
 */
import { useCallback, useSyncExternalStore } from 'react';

const listeners = new Set<() => void>();

function subscribe(fn: () => void) {
  listeners.add(fn);
  window.addEventListener('popstate', fn);
  return () => {
    listeners.delete(fn);
    window.removeEventListener('popstate', fn);
  };
}

/** Trailing slashes are stripped so `/admin/` and `/admin` are one route. */
const currentPath = () => window.location.pathname.replace(/\/+$/, '') || '/';

export function useRoute(): { path: string; navigate: (to: string) => void } {
  const path = useSyncExternalStore(subscribe, currentPath, () => '/');

  const navigate = useCallback((to: string) => {
    if (to === currentPath()) return;
    window.history.pushState(null, '', to);
    for (const fn of listeners) fn();
  }, []);

  return { path, navigate };
}
